import { WSAlert, WSList, WSText } from "@wingspanhq/fe-component-library";
import {
  FormType,
  PayeeEligibilityRequirement,
  PayeeEngagementPaymentsEligibility,
  PayeeEngagementStatus,
  PayeeStatus,
  PayerLinkRequestStatus,
  RequirementStatus,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  queriesForms,
  queriesPayeeEligibilityRequirements,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { useFocusedFeatures } from "../../../../FocusedFeatures";
import { useQueryContractorComplianceEntity } from "../../hooks/useContractorTaxCompliance";
import { selectorContractorTaxDisplay } from "../../selectors/selectorContractorTaxDisplay";
import { buildPayeeDetailsRequirementsPath } from "../../utils";
import { useXWingspanPayerId } from "./useXWingspanPayerId";

// A requirement is "expiring soon" within this window ahead of its expiry —
// the v3 reconstruction of the v1 server-side `RequirementExpirationStatus`
// signal the original alert cascade consumed.
const EXPIRING_SOON_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

export const PayeeDetailsAlerts: React.FC<{
  payeeId: string;
}> = ({ payeeId }) => {
  const history = useHistory();
  const [hideInfo, setHideInfo] = useState<boolean>(false);
  const xWingspanAccount = useXWingspanPayerId();
  const focusedFeatures = useFocusedFeatures();

  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });
  const queryEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQueryAll({
      payeeId,
      xWingspanAccount
    });
  // Requirement INSTANCES for this contractor (across every engagement). Drives
  // the expiring / expired / incomplete derivations. Degrades gracefully: on
  // error `flatData` is undefined → `?? []` → those branches simply don't fire.
  const queryRequirements =
    queriesPayeeEligibilityRequirements.listPayeeEligibilityRequirements.useQueryAll(
      {
        filter: { payeeId: { eq: payeeId } },
        xWingspanAccount
      }
    );

  const payee = queryPayee.data;

  // Tax-share sources mirror `ContractorInformation`: the payer-owned
  // `ComplianceEntity` (payer-supplied W-9) and the payee-owned Auto-W-9 `Form`.
  const queryComplianceEntity = useQueryContractorComplianceEntity(payeeId);
  const queryForm = queriesForms.listForms.useQuery(
    {
      filter: {
        type: { eq: FormType.W9 },
        recipientAccountId: { eq: payee?.payeeAccountId ?? "" },
        isLatest: { eq: true }
      }
    },
    {
      enabled: !!payee?.payeeAccountId,
      select: list => list.data?.[0]
    }
  );

  if (!payee) {
    return null;
  }

  const engagements = queryEngagements.flatData ?? [];
  const requirements = queryRequirements.flatData ?? [];

  const isNotRegistered =
    payee.linkRequestStatus !== PayerLinkRequestStatus.LINKED;
  const isInactive = payee.status === PayeeStatus.DEACTIVATED;

  const now = Date.now();
  const isRequirementExpired = (requirement: PayeeEligibilityRequirement) =>
    requirement.status === RequirementStatus.EXPIRED ||
    (!!requirement.expiresAt &&
      new Date(requirement.expiresAt).getTime() < now);
  const isRequirementExpiringSoon = (
    requirement: PayeeEligibilityRequirement
  ) =>
    !isRequirementExpired(requirement) &&
    !!requirement.expiresAt &&
    new Date(requirement.expiresAt).getTime() - now <= EXPIRING_SOON_WINDOW_MS;

  const expiredRequirements = requirements.filter(isRequirementExpired);
  const expiringRequirements = requirements.filter(isRequirementExpiringSoon);
  const hasPayoutMethodRequirement = requirements.some(
    requirement => requirement.type === RequirementType.PAYOUT_METHOD
  );

  // "Requirements incomplete" keeps the current v3 engagement projection
  // (`areAllRequirementsComplete`) and additionally flags any blocking
  // requirement instance that isn't `Completed`.
  const hasIncompleteRequirements =
    engagements.some(
      engagement => engagement.areAllRequirementsComplete === false
    ) ||
    requirements.some(
      requirement =>
        requirement.isBlocking &&
        requirement.status !== RequirementStatus.COMPLETED
    );

  // Payments eligibility off the active engagement rows. Trust the SDK field —
  // when unprojected (undefined) neither branch fires (safe degradation).
  const activeEngagements = engagements.filter(
    engagement => engagement.status === PayeeEngagementStatus.ACTIVATED
  );
  const hasEligibleEngagements = activeEngagements.some(
    engagement =>
      engagement.paymentsEligibility ===
      PayeeEngagementPaymentsEligibility.ELIGIBLE
  );
  const hasPaymentsEligibilityPending = activeEngagements.some(
    engagement =>
      engagement.paymentsEligibility ===
      PayeeEngagementPaymentsEligibility.NOT_ELIGIBLE
  );

  // Tax identity not shared: a registered contractor for whom neither a
  // payer-supplied `ComplianceEntity` nor a payee-shared W-9 `Form` resolves.
  // Gated on both fetches succeeding so a failed/loading fetch never raises a
  // false "not shared" (safe degradation).
  const taxDisplay = selectorContractorTaxDisplay({
    payee,
    complianceEntity: queryComplianceEntity.data,
    form: queryForm.data
  });
  const isTaxNotShared =
    !isNotRegistered &&
    queryComplianceEntity.isSuccess &&
    queryForm.isSuccess &&
    !taxDisplay;

  const isAllSet =
    !isNotRegistered &&
    !isInactive &&
    !hasIncompleteRequirements &&
    expiredRequirements.length === 0 &&
    expiringRequirements.length === 0 &&
    hasEligibleEngagements;

  const isPaymentsEligibilityPending =
    !isNotRegistered &&
    !isInactive &&
    !hasIncompleteRequirements &&
    hasPaymentsEligibilityPending;

  const showRequirementsIncomplete =
    !isNotRegistered && !isInactive && hasIncompleteRequirements;

  const alert =
    isNotRegistered && !hideInfo ? (
      <WSAlert
        icon="user"
        onDismiss={() => {
          setHideInfo(prev => !prev);
        }}
        size="M"
        theme="info"
        title="Contractor invited"
      >
        <WSText.ParagraphSm weight="book" color="gray700">
          The contractor has been invited by the email provided to sign-up for a
          Wingspan account in order to receive payments from you.
        </WSText.ParagraphSm>
      </WSAlert>
    ) : isTaxNotShared ? (
      <WSAlert
        icon="alert"
        size="M"
        theme="warning"
        title="Tax information not shared"
      >
        <WSText.ParagraphSm weight="book" color="gray700">
          The contractor has not consented to share their tax information with
          you. Payments cannot be processed until they consent. We've notified
          them to complete this step.
        </WSText.ParagraphSm>
      </WSAlert>
    ) : isInactive ? (
      <WSAlert icon="archive" size="M" theme="info" title="Archived">
        <WSText.ParagraphSm weight="book" color="gray700">
          This contractor is currently inactive and cannot be added to new
          engagements or invoices until they are restored. <br />
          Their eligibility will be recalculated if they are restored. Past
          requirement files for this contractor can be viewed here.
        </WSText.ParagraphSm>
      </WSAlert>
    ) : isAllSet ? (
      <WSAlert
        theme="success"
        icon="check-circle"
        title="Contractor is all set for now"
      >
        All eligibility requirements completed. Any new requirements will appear
        here.
      </WSAlert>
    ) : isPaymentsEligibilityPending ? (
      <WSAlert
        theme="warning"
        icon="alert"
        title="Payments eligibility pending"
      >
        The contractor should have at least one active engagement that is
        eligible for payments.
      </WSAlert>
    ) : focusedFeatures.showContractorDetailsTaxOnlyPayerEligibilityWarning ? (
      <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
        The contractor must complete all requirements to receive tax forms.
      </WSAlert>
    ) : expiredRequirements.length > 0 || expiringRequirements.length > 0 ? (
      <>
        {expiredRequirements.length > 0 && (
          <WSAlert
            theme="error"
            icon="alert-circle"
            title="Requirements expired"
            action={{
              text: "View requirements",
              onClick: () => {
                history.push(buildPayeeDetailsRequirementsPath(payeeId));
              }
            }}
          >
            The contractor has {expiredRequirements.length} requirements that
            have expired and require renewal.
          </WSAlert>
        )}
        {expiringRequirements.length > 0 && (
          <WSAlert
            theme="neutral"
            icon="calendar"
            title="Requirements expiring soon"
            action={{
              text: "View requirements",
              onClick: () => {
                history.push(buildPayeeDetailsRequirementsPath(payeeId));
              }
            }}
          >
            The contractor has {expiringRequirements.length} requirements
            expiring soon.
          </WSAlert>
        )}
      </>
    ) : showRequirementsIncomplete ? (
      hasPayoutMethodRequirement ? (
        <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
          The contractor has incomplete requirements that have been assigned to
          them. In order to receive payments for a particular engagement, all
          requirements for that engagement must be complete.
        </WSAlert>
      ) : (
        <WSAlert theme="warning" icon="alert" title="Requirements incomplete">
          The contractor has incomplete requirements that have been assigned to
          them. The contractor must complete all of them to avoid delays in
          working with you.
        </WSAlert>
      )
    ) : null;

  if (!alert) {
    return null;
  }

  return <WSList gap="L">{alert}</WSList>;
};
